import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Save, X, ExternalLink, Image as ImageIcon } from "lucide-react";

const BLANK = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  hero_image: "",
  category: "Cost guides",
  author: "Prime Build Design",
  published: true,
};

const CATEGORIES = ["Cost guides", "Planning & permits", "Project Case Studies", "Renovation tips", "News"];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (img) => (img && img.startsWith("/api/") ? `${BACKEND_URL}${img}` : img);

export default function PostsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/posts/admin/all");
      setItems(data);
    } catch {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ ...BLANK });
  const startEdit = (i) => setEditing({ ...i });
  const cancel = () => setEditing(null);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((cur) => ({ ...cur, hero_image: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!editing.title || !editing.excerpt || !editing.body) {
      toast.error("Title, excerpt and body are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editing.title,
        slug: editing.slug || undefined,
        excerpt: editing.excerpt,
        body: editing.body,
        hero_image: editing.hero_image || undefined,
        category: editing.category || undefined,
        author: editing.author || "Prime Build Design",
        published: !!editing.published,
      };
      if (editing.id) {
        await api.patch(`/posts/${editing.id}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/posts", payload);
        toast.success("Post published");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/posts/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-posts">
      <div className="flex justify-between items-end flex-wrap gap-4 mb-6">
        <div>
          <div className="overline">Content</div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Blog posts</h2>
          <p className="text-sm text-slate-500 mt-1">Cost guides, planning explainers and case studies. Supports <code className="bg-slate-100 px-1">## H2</code>, <code className="bg-slate-100 px-1">**bold**</code>, <code className="bg-slate-100 px-1">_italic_</code>, links and bullet lists.</p>
        </div>
        {!editing && (
          <button onClick={startNew} className="btn-accent text-sm" data-testid="admin-post-new">
            <Plus size={14} /> New post
          </button>
        )}
      </div>

      {editing && (
        <div className="bg-white border border-slate-200 p-6 mb-8" data-testid="admin-post-form">
          <div className="overline mb-4">{editing.id ? `Edit post · ${editing.slug || ""}` : "New post"}</div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="post-title" />
            <Field label="Slug (optional — auto from title)" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testid="post-slug" placeholder="will-be-auto-generated" />
            <div>
              <label className="overline">Category</label>
              <select
                value={editing.category || ""}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                className="field mt-2"
                data-testid="post-category"
              >
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <Field label="Author" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} testid="post-author" />
            <div className="flex items-end md:col-span-2">
              <label className="flex items-center gap-2 text-sm text-slate-700 mb-2">
                <input
                  type="checkbox"
                  checked={!!editing.published}
                  onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                  data-testid="post-published"
                />
                Published
              </label>
            </div>
          </div>

          <div className="mt-5">
            <label className="overline">Excerpt (1–2 sentences for cards)</label>
            <textarea
              className="field mt-2"
              rows={2}
              value={editing.excerpt}
              onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
              data-testid="post-excerpt"
            />
          </div>

          <div className="mt-5">
            <label className="overline">Hero image (optional)</label>
            <div className="mt-2 flex flex-col md:flex-row gap-4 items-start">
              {editing.hero_image ? (
                <img src={resolveImg(editing.hero_image)} alt="Preview" className="w-40 h-28 object-cover border border-slate-200" />
              ) : (
                <div className="w-40 h-28 border border-dashed border-slate-300 flex items-center justify-center text-slate-400 bg-slate-50">
                  <ImageIcon size={20} />
                </div>
              )}
              <div className="flex-1 space-y-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onUpload}
                  disabled={uploading}
                  data-testid="post-image-upload"
                  className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-slate-900 file:text-white file:cursor-pointer file:hover:bg-slate-800"
                />
                <input
                  className="field"
                  placeholder="…or paste an image URL"
                  value={editing.hero_image || ""}
                  onChange={(e) => setEditing({ ...editing, hero_image: e.target.value })}
                  data-testid="post-image-url"
                />
              </div>
            </div>
          </div>

          <div className="mt-5">
            <label className="overline">Body (markdown)</label>
            <textarea
              className="field mt-2 font-mono text-sm leading-relaxed"
              rows={16}
              value={editing.body}
              onChange={(e) => setEditing({ ...editing, body: e.target.value })}
              placeholder={`## Overview\n\nLondon house extensions cost between **£2,800** and **£5,200** per square metre…\n\n- Side-return: £80k–£140k\n- Wrap-around: £120k–£220k\n\n[Get a free quote](/contact).`}
              data-testid="post-body"
            />
          </div>

          <div className="mt-6 flex gap-3">
            <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="admin-post-save">
              <Save size={14} /> {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={cancel} className="btn-ghost text-sm" data-testid="admin-post-cancel">
              <X size={14} /> Cancel
            </button>
            {editing.id && editing.published && (
              <a href={`/blog/${editing.slug}`} target="_blank" rel="noreferrer" className="btn-ghost text-sm" data-testid="admin-post-view">
                <ExternalLink size={14} /> View live
              </a>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200">
        <table className="w-full text-sm" data-testid="posts-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-left">
            <tr>
              <th className="p-4 overline">Title</th>
              <th className="p-4 overline">Category</th>
              <th className="p-4 overline">Updated</th>
              <th className="p-4 overline">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-8 text-center text-slate-500">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="p-12 text-center text-slate-500">No posts yet. Click "New post" to publish your first insight.</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => startEdit(p)} data-testid={`post-row-${p.id}`}>
                <td className="p-4">
                  <div className="font-medium text-slate-900">{p.title}</div>
                  <div className="text-xs text-slate-500">/{p.slug}</div>
                </td>
                <td className="p-4">{p.category || "—"}</td>
                <td className="p-4 text-slate-600 text-xs">
                  {new Date(p.updated_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 text-[10px] font-medium tracking-wide border ${p.published ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-slate-100 border-slate-300 text-slate-700"}`}>
                    {p.published ? "PUBLISHED" : "DRAFT"}
                  </span>
                </td>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => remove(p.id)} aria-label="Delete" className="text-slate-400 hover:text-red-600" data-testid={`post-delete-${p.id}`}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="field mt-2"
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}
